import { DocumentServicesObject } from '@wix/document-services-types';
import type {
  AppInstallOption,
  IntentInstallOptions,
} from '@wix/editor-platform-host-integration-apis';
import { PlatformContext } from '../../../types/platformApi';
import { getIntentInstallServices } from '../intentInstallService';
import {
  AppDependenciesInstallServiceErrorCode,
  createAppDependenciesInstallServiceError,
} from './errors';
import {
  createDependencyAppInstallOption,
  createDependencyInstallOrigin,
  getMissingDependencyAppDefinitionIds,
} from '../appDependenciesInstallData';

const createIntentInstallOptions = (
  rootAppDefinitionId: string,
  dependencyAppDefinitionIds: string[],
): IntentInstallOptions => {
  const origin = createDependencyInstallOrigin(rootAppDefinitionId);

  const appsOptions = dependencyAppDefinitionIds.reduce(
    (acc, dependencyAppDefinitionId) => {
      acc[dependencyAppDefinitionId] =
        createDependencyAppInstallOption(rootAppDefinitionId);
      return acc;
    },
    {} as Record<string, AppInstallOption>,
  );

  return {
    appsToInstall: dependencyAppDefinitionIds,
    isSilent: true,
    disableProgressBar: true,
    shouldInstallPending: false,
    installOrigin: origin,
    appsOptions,
  };
};

export const ensureAppDependenciesInstalled = async (
  context: PlatformContext,
  documentServicesAPI: DocumentServicesObject,
  rootAppDefinitionId: string,
): Promise<void> => {
  const dependencyAppDefinitionIds = getMissingDependencyAppDefinitionIds(
    documentServicesAPI,
    rootAppDefinitionId,
  );

  if (!dependencyAppDefinitionIds.length) {
    return;
  }

  const intentInstallServices = await getIntentInstallServices();

  let result;
  try {
    const intentInstallOptions = createIntentInstallOptions(
      rootAppDefinitionId,
      dependencyAppDefinitionIds,
    );
    result = await intentInstallServices.intentInstall(
      context,
      intentInstallOptions,
    );
  } catch (e) {
    throw createAppDependenciesInstallServiceError(
      AppDependenciesInstallServiceErrorCode.CompanionAppDependenciesInstallFailed,
      'Failed to install companion app dependencies',
    )
      .withRootAppDefinitionId(rootAppDefinitionId)
      .withDependencyAppDefinitionIds(dependencyAppDefinitionIds)
      .withParentError(e as Error);
  }

  if (result.failedApps.length) {
    throw createAppDependenciesInstallServiceError(
      AppDependenciesInstallServiceErrorCode.CompanionAppDependenciesInstallPartialFailure,
      'Some companion app dependencies failed to install',
    )
      .withRootAppDefinitionId(rootAppDefinitionId)
      .withDependencyAppDefinitionIds(dependencyAppDefinitionIds)
      .withFailedAppDefinitionIds(result.failedApps);
  }
};
